// ChatFontScalePreference — チャット欄 (ログ + 入力欄 + 発言小窓 input) の
// 文字サイズだけを拡大する個人設定を localStorage に永続化する infrastructure 層
// ユーティリティ.
//
// 背景 (alpha-feedback-2026-05-31.md §3, ID=E):
//   - α 期受信フィードバック: 「チャット欄の文字がすごく小さい. 最大 300% まで
//     拡大したい」(= アクセシビリティ + 高 DPI / 加齢視力 等の観点).
//   - 初期実装 (commit 0f81f92, 2026-05-31): 既存 `uiScalePreference.ts` の
//     チャット軸を上振れ 4 段 (xxlarge..gigantic) に拡張する案 + 本軸を併存させる
//     2 軸案で議論したが、最終的に「チャット欄の大きさ」(= wrapper 全体 zoom) は
//     既存 splitter (= chatRowPx / memberColPx の drag) と機能重複だったため
//     撤去 (2026-05-31 chat-scale-cleanup). 本「チャット文字サイズ」軸だけが
//     残る形になった.
//
// 設計 (alpha-feedback-2026-05-31.md §3 合意):
//   - 本モジュールは **チャット欄内部の文字サイズだけ** を扱う独立軸.
//   - 段階 6 つ: 85% / 100% / 150% / 200% / 250% / 300%. 上限 300% は要望そのまま.
//     85% は等倍より一段小さくしたい人向けの選択肢として 2026-06-05 に下側へ
//     追加 (= 既存段は据え置き).
//   - 値は CSS の `em` 倍率として ChatPanel の log container + input + popup
//     input に当てる (= 親 .text-xs ベース → ScaleFactor 倍).
//   - 永続化: localStorage 1 key (`oekaki:chat-font-scale`), user-local のみ,
//     サーバ同期なし, protocol 変更なし.
//   - `uiScalePreference.ts` と同流儀の StorageLike 抽象 + probe.
//
// なぜ `uiScalePreference` に edge-add しないか:
//   - 既存 `UiScale` 型 / `uiScaleToCssZoom` / `isUiScale` 関連の test と invariant
//     (= サイドバーの上限 140%) を保つため、軸を分離する.
//   - 仕様要件「影響範囲はチャット欄のみ」をコードレベルで担保する (= panel size
//     は触らず、文字サイズだけ拡大).
//
// Tests: chatFontScalePreference.test.ts (storage-injection style,
// uiScalePreference.test.ts と同流儀).

/**
 * チャット欄文字サイズ段階. 文字列で localStorage に保存される.
 * 値順は「最小 85% → 最大 300%」固定.
 *
 * 2026-06-05: 等倍 (text-xs) でも小さく感じるという要望で、等倍より一段小さい
 * `x0_85` (= 85%) を下側に追加 (= 既存 100%〜300% は据え置き).
 */
export type ChatFontScale = 'x0_85' | 'x1' | 'x1_5' | 'x2' | 'x2_5' | 'x3';

/** デフォルト値 (= 等倍). 既存ユーザは何もしなくても 100% で見える. */
export const DEFAULT_CHAT_FONT_SCALE: ChatFontScale = 'x1';

/** localStorage キー. */
export const CHAT_FONT_SCALE_KEY = 'oekaki:chat-font-scale';

/**
 * ChatFontScale → em 倍率 (= ChatPanel の親 .text-xs に対する CSS em).
 * 値は仕様 §3 で例示された 100% / 150% / 200% / 250% / 300% をそのまま採用.
 * 100% (= 等倍) でフォントサイズが既存の text-xs と一致するように設計してある.
 */
export const chatFontScaleToEm = (scale: ChatFontScale): number => {
  switch (scale) {
    case 'x0_85':
      return 0.85;
    case 'x1':
      return 1.0;
    case 'x1_5':
      return 1.5;
    case 'x2':
      return 2.0;
    case 'x2_5':
      return 2.5;
    case 'x3':
      return 3.0;
  }
};

/**
 * 文字列が有効な ChatFontScale か判定する type guard. unknown / 不正値の
 * defensive narrowing 用.
 */
export const isChatFontScale = (v: unknown): v is ChatFontScale =>
  v === 'x0_85' || v === 'x1' || v === 'x1_5' || v === 'x2' || v === 'x2_5' || v === 'x3';

/**
 * Minimal Storage shape this module uses (uiScalePreference.ts と同流儀).
 * `globalThis.localStorage` matches; tests pass a mock with the same surface.
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ChatFontScalePreferenceStore {
  /** 現在のチャット文字スケールを読み出し. 不在 / 不正 / storage 不可 → default. */
  get(): ChatFontScale;
  /** チャット文字スケールを保存. storage 不可なら no-op (例外を throw しない). */
  set(scale: ChatFontScale): void;
  /** key を削除. テスト / 設定リセット用. storage 不可なら no-op. */
  clear(): void;
}

/**
 * `globalThis.localStorage` をプローブして使用可能なら返す, さもなくば `null`.
 * uiScalePreference.ts の probe を流用 (sentinel write + readback で Safari
 * private browsing / QuotaExceeded を弾く).
 */
const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:chat-font-scale__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreateChatFontScalePreferenceStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall through to no-op if unavailable. Pass `null` to **force** the no-op
   * path (= getter always returns DEFAULT_CHAT_FONT_SCALE, setter silently drops).
   *
   * テストでは小さな `Map` ベースの fake を渡す.
   */
  storage?: StorageLike | null;
}

const readChatFontScale = (storage: StorageLike): ChatFontScale => {
  try {
    const raw = storage.getItem(CHAT_FONT_SCALE_KEY);
    if (raw === null) return DEFAULT_CHAT_FONT_SCALE;
    return isChatFontScale(raw) ? raw : DEFAULT_CHAT_FONT_SCALE;
  } catch {
    return DEFAULT_CHAT_FONT_SCALE;
  }
};

const writeChatFontScale = (storage: StorageLike, scale: ChatFontScale): void => {
  try {
    storage.setItem(CHAT_FONT_SCALE_KEY, scale);
  } catch {
    // QuotaExceeded / SecurityError 等 — 永続化できないだけで動作は継続させる.
  }
};

/**
 * `ChatFontScalePreferenceStore` のインスタンスを構築. storage が unavailable
 * な環境 (SSR / Safari private browsing 厳格モード) でも getter は default を
 * 返すだけで、setter は no-op (= 例外で UI が壊れることはない).
 */
export const createChatFontScalePreferenceStore = (
  opts: CreateChatFontScalePreferenceStoreOptions = {},
): ChatFontScalePreferenceStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    get() {
      if (storage === null) return DEFAULT_CHAT_FONT_SCALE;
      return readChatFontScale(storage);
    },
    set(scale) {
      if (storage === null) return;
      writeChatFontScale(storage, scale);
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(CHAT_FONT_SCALE_KEY);
      } catch {
        // ignore
      }
    },
  };
};
